import { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { BillingInfoFieldName } from '@shipstation/financial-services-ui-components';

import { FlexColumn } from 'ui-components/components/flex/column';
import { Gap } from 'ui-components/components/flex/gap';

import { US } from '../../../constants/country-codes';
import { getBillingInfoFromPaymentMethod } from '../../../financial-services-ui/mapping';
import { usePaymentAndSubscriptionMegaContext } from '../../../pages/settings/payment-and-subscription/context';
import Global from '../../../store/global-selectors';
import {
  CarrierConnectionInteractionAction,
  CarrierConnectionInteractionSection,
  CarrierConnectionPaymentMethodSource,
  Workflows,
} from '../../../tracking/tracking-constants';
import { useTrackWorkflow, WorkflowSteps } from '../../../tracking/use-workflow-tracking';
import { getErrorMessage } from '../../../validation/utilities';
import type { ValidationError } from '../../../validation/validation-error';
import type { Address, WwexFormData } from '../types';
import { AUTO_PAY_ACCEPTED_FIELD, PAYMENT_METHOD_ID_FIELD, TERMS_ACCEPTED_FIELD } from '../wwex-form-validation-schema';

import { AccountInfoSummary } from './account-info-summary';
import { AuctanePayAccountSelect, NEW_PAYMENT_METHOD_VALUE } from './auctane-pay-account-select';
import { AutoPayAcceptanceSection } from './auto-pay-acceptance-section';
import { BillingAddressSummary } from './billing-address-summary';
import { TermsSection } from './terms-section';

const EMPTY_ADDRESS: Address = { line1: '', city: '', state: '', postalCode: '', countryCode: 'US' };

type WwexSummaryViewProps = {
  formData: Partial<WwexFormData>;
  errors: Record<string, ValidationError>;
  showAllErrors: boolean;
  isDisabled: boolean;
  onChange: (data: Partial<WwexFormData>) => void;
  onEditAccountInfo: () => void;
  onEditBillingAddress: () => void;
  onAddPaymentMethod: () => void;
  onPaymentMethodSourceChange: (source: string) => void;
};

export const WwexSummaryView = ({
  formData,
  errors,
  showAllErrors,
  isDisabled,
  onChange,
  onEditAccountInfo,
  onEditBillingAddress,
  onAddPaymentMethod,
  onPaymentMethodSourceChange,
}: WwexSummaryViewProps) => {
  const sellerCountry = useSelector(Global.selectSellerHomeCountry);
  const { paymentMethodDetails } = usePaymentAndSubscriptionMegaContext();
  const trackCarrierConnectionStep = useTrackWorkflow(Workflows.CARRIER_CONNECTION_FLOW);

  const handlePaymentMethodChange = useCallback(
    (paymentMethodId: string, isUserInitiated = true) => {
      if (isDisabled) return;

      if (paymentMethodId === NEW_PAYMENT_METHOD_VALUE) {
        if (isUserInitiated) {
          trackCarrierConnectionStep(WorkflowSteps.INTERACTION, {
            section: CarrierConnectionInteractionSection.PAYMENT,
            action: CarrierConnectionInteractionAction.ADD_NEW_OPENED,
            existing_payment_method_count: paymentMethodDetails.length,
          });
        }
        onAddPaymentMethod();
        return;
      }

      // Re-selecting the already-selected method is a full no-op, not just for tracking: the
      // billing-info derivation below is a pure function of paymentMethodId/formData, both
      // unchanged here, so re-running it would just re-set state to the same values.
      if (paymentMethodId === formData.paymentMethodId) return;

      if (isUserInitiated) {
        onPaymentMethodSourceChange(CarrierConnectionPaymentMethodSource.SWITCHED_EXISTING);
        trackCarrierConnectionStep(WorkflowSteps.CARRIER_CONNECTION_PAYMENT_METHOD_SELECTED);
        trackCarrierConnectionStep(WorkflowSteps.INTERACTION, {
          section: CarrierConnectionInteractionSection.PAYMENT,
          action: CarrierConnectionInteractionAction.METHOD_CHANGED,
          existing_payment_method_count: paymentMethodDetails.length,
        });
      }

      const selectedMethod = paymentMethodDetails.find((method) => method.id === paymentMethodId);
      const billingInfo = selectedMethod?.accountHolderAddress
        ? getBillingInfoFromPaymentMethod(selectedMethod, {
            name: formData.contactFullName ?? '',
            homeCountry: sellerCountry ?? US,
            phone: formData.phoneNumber ?? '',
            companyName: formData.companyName ?? '',
          })
        : null;

      if (!billingInfo) {
        onChange({ ...formData, paymentMethodId });
        return;
      }

      const billingAddress: Address = {
        line1: billingInfo[BillingInfoFieldName.Line1] || '',
        line2: billingInfo[BillingInfoFieldName.Line2] || '',
        line3: '',
        city: billingInfo[BillingInfoFieldName.City] || '',
        state: billingInfo[BillingInfoFieldName.State] || '',
        postalCode: billingInfo[BillingInfoFieldName.PostalCode] || '',
        countryCode: billingInfo[BillingInfoFieldName.CountryCode] || 'US',
      };

      onChange({
        ...formData,
        paymentMethodId,
        billingFullName: billingInfo[BillingInfoFieldName.FullName] || '',
        billingCompanyName: billingInfo[BillingInfoFieldName.Company] || '',
        billingAddress,
      });
    },
    [
      formData,
      isDisabled,
      onAddPaymentMethod,
      onChange,
      onPaymentMethodSourceChange,
      paymentMethodDetails,
      sellerCountry,
      trackCarrierConnectionStep,
    ],
  );

  // Keep a ref to the latest handler so the pre-select effect below doesn't need to depend on it —
  // handlePaymentMethodChange gets a new identity on every formData change (e.g. toggling checkboxes)
  const handlePaymentMethodChangeRef = useRef(handlePaymentMethodChange);
  useEffect(() => {
    handlePaymentMethodChangeRef.current = handlePaymentMethodChange;
  });

  const hasPreselectedRef = useRef(false);

  // Pre-select the seller's first connected payment method, seeding its billing address read-only
  useEffect(() => {
    if (hasPreselectedRef.current || formData.paymentMethodId || paymentMethodDetails.length === 0) return;

    hasPreselectedRef.current = true;
    handlePaymentMethodChangeRef.current(paymentMethodDetails[0].id, false);
  }, [formData.paymentMethodId, paymentMethodDetails]);

  const handleAutoPayChange = useCallback(
    (accepted: boolean) => onChange({ ...formData, autoPayAccepted: accepted }),
    [formData, onChange],
  );

  const handleTermsChange = useCallback((accepted: boolean) => onChange({ ...formData, termsAccepted: accepted }), [formData, onChange]);

  const hasAccountInfoError = !!(errors.contactFullName || errors.companyName || errors.email || errors.phoneNumber);
  const hasBillingAddressError = showAllErrors && !!(errors.billingFullName || errors.billingCompanyName || errors.billingAddress);

  return (
    <FlexColumn gap={Gap.Space16}>
      <AccountInfoSummary
        companyName={formData.companyName ?? ''}
        contactFullName={formData.contactFullName ?? ''}
        email={formData.email ?? ''}
        hasError={hasAccountInfoError}
        isDisabled={isDisabled}
        onEdit={onEditAccountInfo}
        phoneNumber={formData.phoneNumber ?? ''}
      />

      <AuctanePayAccountSelect
        disabled={isDisabled}
        errorMessage={showAllErrors ? getErrorMessage(errors[PAYMENT_METHOD_ID_FIELD]) : undefined}
        onChange={handlePaymentMethodChange}
        showError={showAllErrors && !!errors[PAYMENT_METHOD_ID_FIELD]}
        value={formData.paymentMethodId ?? ''}
      />

      {formData.paymentMethodId && (
        <BillingAddressSummary
          billingAddress={formData.billingAddress ?? EMPTY_ADDRESS}
          billingFullName={formData.billingFullName ?? ''}
          hasError={hasBillingAddressError}
          isDisabled={isDisabled}
          onEdit={onEditBillingAddress}
        />
      )}

      <AutoPayAcceptanceSection
        error={showAllErrors ? errors[AUTO_PAY_ACCEPTED_FIELD] : undefined}
        isAccepted={formData.autoPayAccepted ?? false}
        isDisabled={isDisabled}
        onAcceptChange={handleAutoPayChange}
      />

      <TermsSection
        error={showAllErrors ? errors[TERMS_ACCEPTED_FIELD] : undefined}
        isAccepted={formData.termsAccepted ?? false}
        isDisabled={isDisabled}
        onAcceptChange={handleTermsChange}
      />
    </FlexColumn>
  );
};
